import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useSearchParams } from 'react-router-dom';
import { collection, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import PortalHeader from '../../components/Portal/PortalHeader';
import WaButton from '../../components/wa/WaButton';
import {
	buildPrintLessons,
	dayCardHeading,
	formatRunRange,
	groupByDay,
	groupByTeacher,
	groupDaysByWeek,
	isReleasedRun,
	runOptionLabel,
	runRangeKeys,
	sortRunsNewestFirst,
} from '../../components/Portal/Scheduling/scheduleRuns';
import { Avatar, uidForTeacherId } from '../../components/Portal/Work/Avatar';
import { LOCATIONS } from './Scheduling/locations';
import './SchedulePrint.css';

/**
 * A printable copy of one schedule run (Anthony, 2026-10-10: "I need a printed
 * copy of it somewhere for my reference from an admin perspective").
 *
 * Any run, draft or released — printing never depends on release. A draft says
 * DRAFT in its heading so a sheet on the desk cannot pass for what teachers see.
 *
 * Admin-only by route, like the board it comes from. Read-only: it writes
 * nothing.
 *
 * PRINTING. The sheet is rendered twice: once in the page for the screen, and
 * once through a portal straight into <body>, which is the only copy the print
 * stylesheet shows. That keeps the portal chrome (sidebar, top bar, and any
 * ancestor with a fixed height or overflow) out of the printout without this
 * page knowing a single layout class name.
 */

const RUN_LIST_LIMIT = 30;

// Location colours come from the studio's one palette (Scheduling/locations.js),
// resolved STRICTLY: its own getLocationColor() falls back to Lakeville blue for
// anything unknown, and a confident wrong colour is worse than none (the same
// reasoning as TodayPage's locationColor). The chips set BOTH fill and ink, so
// they read the same in every theme.
const locationTone = (key) => LOCATIONS.find((l) => l.value.toUpperCase() === key) || null;

const LocationChip = ({ location, label }) => {
	const tone = locationTone(location);
	return (
		<span
			className='sp-loc'
			style={tone ? { '--sp-loc-fill': tone.fill, '--sp-loc-ink': tone.text, '--sp-loc-bar': tone.bar } : undefined}>
			{label}
		</span>
	);
};

const Teacher = ({ teacherId, name, size = 28 }) => (
	<span className='sp-person'>
		<Avatar uid={teacherId ? uidForTeacherId(teacherId) : null} name={name} size={size} className='sp-face' decorative />
		<span className='sp-person__name'>{name}</span>
	</span>
);

const ByDay = ({ lessons }) => (
	<>
		{groupDaysByWeek(groupByDay(lessons)).map((week) => (
			<section className='sp-week' key={week.weekStart}>
				<h3 className='sp-week__title'>{week.label}</h3>
				<div className='sp-days'>
					{week.days.map((day) => {
						const heading = dayCardHeading(day.dayKey);
						return (
							<article className='sp-day' key={day.dayKey} aria-label={day.label}>
								<header className='sp-day__head'>
									<span className='sp-day__weekday'>{heading.weekday}</span>
									<span className='sp-day__date'>{heading.date}</span>
									{/* Print keeps the full words; the two-part heading is for the screen. */}
									<span className='sp-print-only'>{day.label}</span>
								</header>
								{day.locations.map((loc) => {
									const tone = locationTone(loc.location);
									const single = loc.teachers.length === 1;
									return (
										<div
											className='sp-block'
											key={loc.location}
											style={tone ? { '--sp-loc-bar': tone.bar } : undefined}>
											<div className='sp-block__head'>
												<LocationChip location={loc.location} label={loc.label} />
												{single ? (
													<Teacher teacherId={loc.lessons[0].teacherId} name={loc.teachers[0]} />
												) : (
													<span className='sp-split'>Split day</span>
												)}
											</div>
											<ul className='sp-lessons'>
												{loc.lessons.map((lesson) => (
													<li className='sp-lesson' key={lesson.eventId}>
														<span className='sp-lesson__time'>{lesson.time}</span>
														<span className='sp-lesson__student'>{lesson.studentName}</span>
														{/* Only a split day names the teacher per lesson;
														    otherwise the block heading already does. */}
														{!single && (
															<Teacher teacherId={lesson.teacherId} name={lesson.teacherName} size={22} />
														)}
													</li>
												))}
											</ul>
										</div>
									);
								})}
							</article>
						);
					})}
				</div>
			</section>
		))}
	</>
);

const ByTeacher = ({ lessons }) => (
	<div className='sp-teachers'>
		{groupByTeacher(lessons).map((teacher) => (
			<section className='sp-teacher-card' key={teacher.teacherId || 'unassigned'}>
				<header className='sp-teacher-card__head'>
					<Teacher teacherId={teacher.teacherId} name={teacher.teacherName} size={44} />
					<span className='sp-teacher-card__meta'>
						{teacher.lessonCount} lesson{teacher.lessonCount === 1 ? '' : 's'} · {teacher.dayCount} day
						{teacher.dayCount === 1 ? '' : 's'}
					</span>
				</header>
				{teacher.days.map((day) => {
					const heading = dayCardHeading(day.dayKey);
					const locations = [...new Map(day.lessons.map((l) => [l.location, l.locationLabel])).entries()];
					return (
						<div className='sp-tday' key={day.dayKey}>
							<div className='sp-tday__head'>
								<span className='sp-tday__date'>
									<strong>{heading.weekday}</strong> {heading.date}
								</span>
								{locations.map(([key, label]) => (
									<LocationChip key={key} location={key} label={label} />
								))}
							</div>
							<ul className='sp-lessons'>
								{day.lessons.map((lesson) => (
									<li className='sp-lesson' key={lesson.eventId}>
										<span className='sp-lesson__time'>{lesson.time}</span>
										<span className='sp-lesson__student'>{lesson.studentName}</span>
									</li>
								))}
							</ul>
						</div>
					);
				})}
			</section>
		))}
	</div>
);

const Sheet = ({ run, lessons, grouping, totals }) => (
	<div className='sp-sheet'>
		<header className='sp-sheet__head'>
			<div className='sp-sheet__titles'>
				<h2 className='sp-sheet__title'>{formatRunRange(run)}</h2>
				{isReleasedRun(run) ? (
					<span className='sp-status is-released'>Released · what teachers see</span>
				) : (
					<span className='sp-status is-draft'>DRAFT · not visible to teachers</span>
				)}
			</div>
			<ul className='sp-totals' aria-label='Lessons per teacher'>
				<li className='sp-total sp-total--all'>
					<strong>{lessons.length}</strong> lesson{lessons.length === 1 ? '' : 's'}
				</li>
				{totals.map((t) => (
					<li className='sp-total' key={t.teacherName}>
						<Avatar uid={t.teacherId ? uidForTeacherId(t.teacherId) : null} name={t.teacherName} size={24} className='sp-face' decorative />
						{t.teacherName} <strong>{t.lessonCount}</strong>
					</li>
				))}
			</ul>
		</header>
		{lessons.length === 0 ? (
			<p className='sp-empty'>No lessons in this schedule's range.</p>
		) : grouping === 'teacher' ? (
			<ByTeacher lessons={lessons} />
		) : (
			<ByDay lessons={lessons} />
		)}
	</div>
);

const SchedulePrint = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const requestedRunId = searchParams.get('run') || '';
	const grouping = searchParams.get('by') === 'teacher' ? 'teacher' : 'day';

	const [runs, setRuns] = useState([]);
	const [teacherNameById, setTeacherNameById] = useState(new Map());
	const [events, setEvents] = useState([]);
	const [assignments, setAssignments] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	// Runs + teachers once. ALL teachers, not only active ones: an old run can
	// name someone who has since left, and a sheet saying "Unknown teacher"
	// for a real lesson is worse than a former colleague's name.
	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				const [runSnap, teacherSnap] = await Promise.all([
					getDocs(query(collection(db, 'scheduleRuns'), orderBy('createdAt', 'desc'), limit(RUN_LIST_LIMIT))),
					getDocs(collection(db, 'teachers')),
				]);
				if (cancelled) return;
				setRuns(sortRunsNewestFirst(runSnap.docs.map((d) => ({ id: d.id, ...d.data() }))));
				setTeacherNameById(new Map(teacherSnap.docs.map((d) => [d.id, d.data().name || d.id])));
			} catch (err) {
				console.error('Could not load schedule runs:', err);
				if (!cancelled) setError(err.message || 'Could not load schedule runs.');
			}
		})();
		return () => {
			cancelled = true;
		};
	}, []);

	const run = useMemo(
		() => (requestedRunId ? runs.find((r) => r.id === requestedRunId) : runs[0]) || null,
		[runs, requestedRunId]
	);

	useEffect(() => {
		if (!run) {
			if (runs.length > 0 || error) setLoading(false);
			return undefined;
		}
		let cancelled = false;
		setLoading(true);
		(async () => {
			try {
				const { from, to } = runRangeKeys(run);
				const [eventSnap, assignmentSnap] = await Promise.all([
					getDocs(
						query(
							collection(db, 'sourceEvents'),
							where('start', '>=', new Date(`${from}T00:00:00Z`)),
							where('start', '<=', new Date(`${to}T23:59:59Z`)),
							orderBy('start', 'asc')
						)
					),
					getDocs(query(collection(db, 'scheduleAssignments'), where('runId', '==', run.id))),
				]);
				if (cancelled) return;
				setEvents(eventSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
				setAssignments(assignmentSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
			} catch (err) {
				console.error('Could not load the schedule:', err);
				if (!cancelled) setError(err.message || 'Could not load the schedule.');
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [run, runs.length, error]);

	const lessons = useMemo(() => {
		if (!run) return [];
		const { from, to } = runRangeKeys(run);
		const assignmentsByEventId = new Map(assignments.map((a) => [a.sourceEventId, a]));
		return buildPrintLessons({ events, assignmentsByEventId, from, to, teacherNameById });
	}, [run, events, assignments, teacherNameById]);

	const totals = useMemo(
		() =>
			groupByTeacher(lessons).map((t) => ({
				teacherId: t.teacherId,
				teacherName: t.teacherName,
				lessonCount: t.lessonCount,
			})),
		[lessons]
	);

	const setParam = (key, value) => {
		const next = new URLSearchParams(searchParams);
		if (value) next.set(key, value);
		else next.delete(key);
		setSearchParams(next, { replace: true });
	};

	const ready = run && !loading && !error;

	return (
		<div className='sp-page'>
			<PortalHeader title='Schedule' subtitle='Any schedule, draft or released, to read or print' />

			{/* .portal-content supplies no side padding; pages bring their own,
			    and this matches the scheduling page's .ts-shell. */}
			<div className='sp-shell'>

			<div className='sp-controls'>
				<Link className='sp-back' to='/portal/work/teacher-scheduling'>
					← Back to scheduling
				</Link>
				<label className='sp-field'>
					<span>Schedule</span>
					<select value={run?.id || ''} onChange={(e) => setParam('run', e.target.value)} disabled={runs.length === 0}>
						{runs.map((r) => (
							<option key={r.id} value={r.id}>
								{runOptionLabel(r)}
							</option>
						))}
					</select>
				</label>
				<div className='sp-toggle' role='group' aria-label='Group the schedule'>
					<button
						type='button'
						className={`sp-toggle__btn${grouping === 'day' ? ' is-active' : ''}`}
						aria-pressed={grouping === 'day'}
						onClick={() => setParam('by', '')}>
						By day
					</button>
					<button
						type='button'
						className={`sp-toggle__btn${grouping === 'teacher' ? ' is-active' : ''}`}
						aria-pressed={grouping === 'teacher'}
						onClick={() => setParam('by', 'teacher')}>
						By teacher
					</button>
				</div>
				<WaButton
					variant='brand'
					onClick={() => {
						if (ready) window.print();
					}}
					disabled={!ready}>
					Print
				</WaButton>
			</div>

			{error && (
				<p className='sp-message is-error' role='alert'>
					Could not load the schedule. Please tell Anthony right away.
					<span className='sp-message__detail'>{error}</span>
				</p>
			)}
			{!error && loading && <p className='sp-message'>Loading the schedule…</p>}
			{!error && !loading && !run && <p className='sp-message'>No schedule has been generated yet.</p>}

			{ready && (
				<>
					<Sheet run={run} lessons={lessons} grouping={grouping} totals={totals} />
					{createPortal(
						<div className='sp-print-root'>
							<Sheet run={run} lessons={lessons} grouping={grouping} totals={totals} />
						</div>,
						document.body
					)}
				</>
			)}
			</div>
		</div>
	);
};

export default SchedulePrint;
